import React, { useState } from 'react'
import { CoachHeader, CoachHeaderLight } from '../CoachHeader'
import { TranslationProvider } from '../../shared/TranslationProvider'
import translations from '../locales.json'
import { useTranslation } from '../../../hooks/useTranslation'
import safeParse from '~/lib/safeParse'
import CoachCard from './CoachCard'
import AccoladeCards, { LiaisonAccoladeCards } from './AccoladeCards'
import { CoachingArticle } from './CoachingArticle'
import YnsIcon from '~/components/icons/YnsIcon'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import UpdateCandidateProfile from '~/components/UpdateCandidateProfile'
const queryClient = new QueryClient()
import { Button } from '~/lib/ui/button'

const Index = ({ locale, ...props }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider
        locale={locale}
        translations={translations}
        scope="javascript.components.coach_services.OutplacementBenefits.MeetYourCoach"
      >
        <MeetYourCoach {...props} />
      </TranslationProvider>
    </QueryClientProvider>
  )
}
export default Index

const renderHeader = (t, select_program, liaison) => {
  if (liaison && select_program) return t('accolades.select_liaison_team')
  if (liaison) return t('accolades.coach_liaison_team')
  return t('header.personal_career_coach')
}

function MeetYourCoach({
  coach,
  coach_photo,
  transparent_coach_photo,
  coach_quote,
  coach_bio,
  accolades,
  select_premium_only,
  select_program,
  what_to_expect_one_to_one_coaching,
  liaison,
  liaison_image_url,
  linkedin_url,
  profile_incomplete,
}) {
  const { yearsExperience, numClientsSupported } = accolades

  const { t } = useTranslation()

  const isEmptyHtml = (str) => !str || str.replace(/<[^>]*>/g, '').trim() === ''
  const quote = isEmptyHtml(coach_quote) ? t('liaison_quote') : coach_quote

  const satisfactionRating = accolades.satisfactionRating

  return (
    <div className="m-4 flex flex-col gap-10 font-roboto xl:m-12">
      <div className="flex flex-col gap-6">
        <CoachHeader className="flex flex-col xl:block">
          <CoachHeaderLight>
            {liaison ? t('header.liaison_your') : t('header.meet_your')}
          </CoachHeaderLight>{' '}
          {renderHeader(t, select_program, liaison)}
        </CoachHeader>

        <CoachCard
          coach={coach}
          coach_photo={coach_photo}
          transparent_coach_photo={transparent_coach_photo}
          accolades={accolades}
          liaison={liaison}
          liaison_image_url={liaison_image_url}
          linkedin_url={linkedin_url}
          selectProgram={select_program}
        />

        <div className="col-span-3 grid grid-cols-3 gap-6 xl:hidden">
          {liaison ? (
            <LiaisonAccoladeCards />
          ) : (
            <AccoladeCards
              yearsExperience={yearsExperience}
              numClientsSupported={numClientsSupported}
              satisfactionRating={satisfactionRating}
            />
          )}
        </div>

        <div className="grid grid-cols-11 font-roboto xl:gap-20">
          {liaison ? (
            <LiaisonDescription
              profileIncomplete={profile_incomplete}
              selectProgram={select_program}
            />
          ) : (
            <CoachBio t={t} coach={coach} coach_bio={coach_bio} />
          )}
          <div className="order-first col-span-12 flex h-fit min-h-[14rem] items-center justify-start rounded-[20px] bg-[#E0F2F9] bg-intoo-logo bg-right bg-no-repeat p-8 xl:order-last xl:col-span-5">
            <div className="normalized-quote-text">
              {safeParse(quote, true)}
            </div>
          </div>
        </div>

        {select_premium_only && (
          <CoachingArticle article={what_to_expect_one_to_one_coaching} />
        )}
      </div>
    </div>
  )
}

const LiaisonDescription = ({ profileIncomplete, selectProgram }) => {
  const { t } = useTranslation('understanding_the_process')

  const list = t('list', { returnObjects: true })

  const [showUpdateProfileDialog, setShowUpdateProfileDialog] = useState(false)

  const toggleShowUpdateProfileDialog = () => {
    setShowUpdateProfileDialog(!showUpdateProfileDialog)
  }

  return (
    <div className="col-span-11 mt-6 flex flex-col gap-4 xl:col-span-6 xl:mt-0">
      <div className="text-3.5xl font-bold text-intoo-green-coachcta">
        {t('title')}:
      </div>
      <div>
        {selectProgram ? safeParse(t('select_description')) : t('description')}
      </div>
      <div className="flex flex-col gap-4">
        {list.map((item, index) => (
          <div className="flex items-center gap-2" key={index}>
            <YnsIcon name={'coach-services-circle'} />
            {item}
          </div>
        ))}
      </div>
      {profileIncomplete && (
        <>
          <UpdateCandidateProfile
            selectProgram={selectProgram}
            showUpdateProfileDialog={showUpdateProfileDialog}
            toggleShowUpdateProfileDialog={toggleShowUpdateProfileDialog}
          >
            <div className="my-4">{t('liaison_noinfo')}</div>
          </UpdateCandidateProfile>

          <Button
            variant="coachcta"
            onClick={() => toggleShowUpdateProfileDialog()}
          >
            {t('update_candidate_profile')}
          </Button>
        </>
      )}
    </div>
  )
}

const CoachBio = ({ t, coach, coach_bio }) => {
  return (
    <div className="col-span-11 mt-6 flex flex-col xl:col-span-6 xl:mt-0">
      <h2 className="mb-3 text-2xl font-normal text-intoo-green-coachcta">
        {t('about.about_coach', { first_name: coach.first_name })}
      </h2>
      <div className="normalized-coach-bio-text">
        {safeParse(coach_bio, true)}
      </div>
    </div>
  )
}
